import { createTheme } from "@mui/material/styles";

/**
 * Portant design language, ported from the marketing site
 * (www.portant.co: css/input.css + tailwind.config.js) into a MUI theme.
 *
 * The signatures we carry over:
 *  - DM Sans for everything, IBM Plex Mono for the uppercase "eyebrow" labels.
 *  - A warm off-white canvas (#f6f5f4) instead of stark white.
 *  - Ink-black (#0c0c0c) as the primary action colour, not blue. Blue (#1875fb)
 *    is the product accent used for links and highlights.
 *  - Cards are white with a hairline parchment border and a soft lift on hover.
 *  - Rounded-lg (8px) corners, tight negative letter-spacing on headings,
 *    weight-500 buttons with no ALL-CAPS transform.
 */

// Brand tokens (mirror --portant-* in css/input.css).
export const portant = {
  white: "#ffffff",
  canvas: "#f6f5f4",
  paper: "#f0efed",
  notebook: "#ebe8e4",
  parchment: "#e2dad2",
  darkParchment: "#c4b9ae",
  charred: "#94908a",
  toasted: "#777169",
  burnt: "#4b4844",
  ash: "#2a2826",
  charcoal: "#1c1c1c",
  inkBlack: "#0c0c0c",
  lightBlue: "#91e6fe",
  teal: "#40dffc",
  brightBlue: "#0280ff",
  blue: "#1875fb",
  boldBlue: "#0044f1",
  navy: "#022235",
  yellow: "#ffaa00",
  midOrange: "#ff8550",
  lightOrange: "#ffe4d8",
  orange: "#ff4800",
  red: "#e40a13",
  lightGreen: "#dcfce7",
} as const;

const sans = `"DM Sans", system-ui, -apple-system, "Segoe UI", sans-serif`;
export const mono = `"IBM Plex Mono", ui-monospace, "Cascadia Code", monospace`;

// Warm hover treatment for the ink-black primary button (matches .btn-primary:hover).
const primaryHoverGradient = `radial-gradient(at 75% 0%, ${portant.burnt} 0%, ${portant.inkBlack} 70%)`;

const portantTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: portant.inkBlack,
      dark: portant.charcoal,
      light: portant.burnt,
      contrastText: portant.white,
    },
    secondary: {
      main: portant.blue,
      dark: portant.boldBlue,
      light: portant.brightBlue,
      contrastText: portant.white,
    },
    error: { main: portant.red },
    warning: { main: portant.orange },
    success: { main: "#1b6b4a" },
    info: { main: portant.blue },
    background: {
      default: portant.canvas,
      paper: portant.white,
    },
    text: {
      primary: portant.inkBlack,
      secondary: portant.toasted,
      disabled: portant.charred,
    },
    divider: portant.parchment,
  },

  shape: { borderRadius: 8 },

  typography: {
    fontFamily: sans,
    // Eyebrow / kicker label — IBM Plex Mono, uppercase, tracked out.
    overline: {
      fontFamily: mono,
      fontSize: "0.6875rem",
      fontWeight: 600,
      letterSpacing: "0.1rem",
      textTransform: "uppercase",
      lineHeight: 1.4,
    },
    h1: { fontFamily: sans, fontWeight: 600, letterSpacing: "-0.05rem", lineHeight: 1.05 },
    h2: { fontFamily: sans, fontWeight: 600, letterSpacing: "-0.04rem", lineHeight: 1.1 },
    h3: { fontFamily: sans, fontWeight: 600, letterSpacing: "-0.03rem", lineHeight: 1.15 },
    h4: { fontFamily: sans, fontWeight: 600, letterSpacing: "-0.02rem", lineHeight: 1.2 },
    h5: { fontFamily: sans, fontWeight: 600, letterSpacing: "-0.015rem", lineHeight: 1.25 },
    h6: { fontFamily: sans, fontWeight: 600, letterSpacing: "-0.01rem", lineHeight: 1.3 },
    subtitle1: { fontWeight: 600, letterSpacing: "-0.01rem" },
    subtitle2: { fontWeight: 600, letterSpacing: "-0.005rem" },
    body1: { letterSpacing: "-0.005rem", lineHeight: 1.55 },
    body2: { letterSpacing: "0", lineHeight: 1.55 },
    button: { fontWeight: 500, letterSpacing: "-0.025em", textTransform: "none" },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: portant.canvas,
          color: portant.inkBlack,
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          letterSpacing: "-0.025em",
          textTransform: "none",
          transition:
            "background-color 0.15s ease, background-image 0.2s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
        },
        sizeSmall: { height: "2.25rem", padding: "0.375rem 0.875rem", fontSize: "0.875rem" },
        sizeMedium: { height: "2.5rem", padding: "0.5rem 1.25rem", fontSize: "0.9375rem" },
        sizeLarge: { height: "3rem", padding: "0.625rem 1.5rem", fontSize: "1rem" },
        // Primary CTA — ink-black fill with a warm radial hover (.btn-primary).
        containedPrimary: {
          backgroundColor: portant.inkBlack,
          color: portant.white,
          border: "2px solid rgba(12, 12, 12, 0.2)",
          "&:hover": {
            backgroundImage: primaryHoverGradient,
            borderColor: "rgba(0, 0, 0, 0.25)",
          },
        },
        // Secondary — white with a hairline ink border (.btn-secondary).
        outlined: {
          backgroundColor: portant.white,
          color: portant.inkBlack,
          borderColor: "rgba(12, 12, 12, 0.1)",
          "&:hover": {
            backgroundColor: portant.white,
            borderColor: "rgba(12, 12, 12, 0.2)",
          },
        },
        text: {
          color: portant.inkBlack,
          "&:hover": { backgroundColor: portant.notebook },
        },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${portant.parchment}`,
          backgroundColor: portant.white,
          backgroundImage: "none",
          transition:
            "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s ease",
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          "&:hover": { transform: "translateY(-2px)" },
          transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
        outlined: { borderColor: portant.parchment },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0, color: "transparent" },
      styleOverrides: {
        root: {
          backgroundColor: portant.white,
          color: portant.inkBlack,
          borderBottom: `1px solid ${portant.parchment}`,
          boxShadow: "none",
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: portant.white,
          borderRadius: 8,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: portant.parchment },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: portant.darkParchment },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: portant.blue,
            borderWidth: 1.5,
          },
        },
        input: { "&::placeholder": { color: portant.charred, opacity: 1 } },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500, letterSpacing: "-0.01rem", borderRadius: 6 },
        outlined: { borderColor: portant.parchment },
      },
    },

    MuiDivider: { styleOverrides: { root: { borderColor: portant.parchment } } },

    MuiAvatar: {
      styleOverrides: {
        root: { backgroundColor: portant.inkBlack, color: portant.white, fontWeight: 600 },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          color: portant.burnt,
          borderRadius: 8,
          "&:hover": { backgroundColor: portant.notebook, color: portant.inkBlack },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: portant.inkBlack,
          fontFamily: sans,
          fontSize: "0.75rem",
          borderRadius: 6,
        },
        arrow: { color: portant.inkBlack },
      },
    },
  },
});

export default portantTheme;
